'use client';

import React, { useEffect, useState, useContext, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../../context/AuthContext';
import { supabase } from '../../supabaseClient';
import Layout from '../Layout';
import MeasurementsMap from '../../components/MeasurementMap';
import { 
  ChevronUpIcon, 
  ChevronDownIcon, 
  FunnelIcon, 
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisVerticalIcon,
  ArrowPathIcon,
  Bars3BottomLeftIcon,
  InformationCircleIcon,
  MapIcon,
  Squares2X2Icon,
  PaintBrushIcon,
  ChartBarIcon,
  ClockIcon,
  UserIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';
import { EyeIcon as EyeIconSolid, MapPinIcon, FolderOpenIcon } from '@heroicons/react/20/solid';

const MAX_RECORDS = 50000;
const MAX_MAP_MARKERS = 10000;
const ROW_HEIGHT = 22;
const VISIBLE_ROWS = 10;
const TABLE_HEIGHT = ROW_HEIGHT * VISIBLE_ROWS;


export default function MeasurementData() {

      // Column definitions with enhanced metadata matching Supabase structure
const columns = [
  { 
    key: 'LocalTimeOfMeasurement', 
    label: 'Local Time', 
    sortable: true, 
    filterable: true,
    width: 160,
    minWidth: 140,
    type: 'datetime',
    format: (value) => value ? new Date(value).toLocaleString() : ''
  },
  { 
    key: 'project_display_name', 
    label: 'Project', 
    sortable: true, 
    filterable: true,
    width: 180,
    minWidth: 120,
    type: 'project'  // Special type for project filtering
  },
  { 
    key: 'operator_name', 
    label: 'Uploaded by', 
    sortable: false, 
    filterable: true,
    width: 140,
    minWidth: 100,
    type: 'text'
  },
  { 
    key: 'Density', 
    label: 'Density', 
    sortable: true, 
    filterable: true,
    width: 90,
    minWidth: 70,
    type: 'number',
    format: (value) => value?.toFixed(1) || ''
  },
  { 
    key: 'SurfaceTemp', 
    label: 'Surface Temp', 
    sortable: true, 
    filterable: true,
    width: 110,
    minWidth: 90,
    type: 'number',
   format: (value) => value?.toFixed(1) || ''
  },
  { 
    key: 'Compaction', 
    label: 'Compaction', 
    sortable: true, 
    filterable: true,
    width: 100,
    minWidth: 80,
    type: 'number',
    format: (value) => value?.toFixed(1) || ''
  },
  { 
    key: 'Voids', 
    label: 'Voids', 
    sortable: true, 
    filterable: true,
    width: 80,
    minWidth: 60,
    type: 'number',
    format: (value) => value?.toFixed(1) || ''
  },
  { 
    key: 'full_description', 
    label: 'Description', 
    sortable: false, 
    filterable: true,
    width: 200,
    minWidth: 150,
    type: 'text',
    subColumns: ['Description1', 'Description2']
  },
  { 
    key: 'full_location', 
    label: 'Location', 
    sortable: false, 
    filterable: true,
    width: 180,
    minWidth: 120,
    type: 'text',
    subColumns: ['Location1', 'Location2']
  },
  { 
    key: 'Station', 
    label: 'Station', 
    sortable: true, 
    filterable: true,
    width: 100,
    minWidth: 70,
    type: 'text'
  },
  { 
    key: 'MixName', 
    label: 'Mix Name', 
    sortable: true, 
    filterable: true,
    width: 120,
    minWidth: 90,
    type: 'text'
  },
  { 
    key: 'gauge_type', 
    label: 'Gauge Type', 
    sortable: true, 
    filterable: true,
    width: 110,
    minWidth: 90,
    type: 'text'
  },
  { 
    key: 'Operator', 
    label: 'Operator', 
    sortable: true, 
    filterable: true,
    width: 140,
    minWidth: 100,
    type: 'text'
  },

];

  const [units, setUnits] = useState({ Density: 'Unknown', SurfaceTemp: 'Unknown' });
  const [selectedMapMeasurement, setSelectedMapMeasurement] = useState(null);



// Filter operators by data type
const filterOperators = {
  text: [
    { value: 'eq', label: '=', needsValue: true },
    { value: 'neq', label: '≠', needsValue: true },
    { value: 'ilike', label: 'contains', needsValue: true },
    { value: 'not.ilike', label: 'does not contain', needsValue: true },
    { value: 'is', label: 'is empty', needsValue: false },
    { value: 'not.is', label: 'is not empty', needsValue: false },
    { value: 'in', label: 'is one of', needsValue: true },
    { value: 'not.in', label: 'is not one of', needsValue: true }
  ],
  number: [
    { value: 'eq', label: '=', needsValue: true },
    { value: 'neq', label: '≠', needsValue: true },
    { value: 'gt', label: '>', needsValue: true },
    { value: 'gte', label: '≥', needsValue: true },
    { value: 'lt', label: '<', needsValue: true },
    { value: 'lte', label: '≤', needsValue: true },
    { value: 'is', label: 'is empty', needsValue: false },
    { value: 'not.is', label: 'is not empty', needsValue: false }
  ],
  datetime: [
    { value: 'eq', label: 'on', needsValue: true },
    { value: 'lt', label: 'before', needsValue: true },
    { value: 'gt', label: 'after', needsValue: true },
  ],
  project: [
    { value: 'eq', label: '=', needsValue: true }
  ]
};

// Fixed colors for pass/fail conditional formatting
const PASS_COLOR = { bg: '#dcfce7', text: '#16a34a' }; // Light Green
const FAIL_COLOR = { bg: '#fef2f2', text: '#dc2626' }; // Light Red

// Enhanced Toggle Component following the exact pattern
const MapToggle = ({ isMapVisible, onChange }) => {
  return (
    <label className={`group relative inline-flex w-12 h-6 shrink-0 rounded-full p-0.5 ring-1 ring-gray-900/5 transition-colors duration-200 ease-in-out cursor-pointer ${isMapVisible ? 'bg-green-600' : 'bg-gray-200'}`}>
      <span className={`relative w-5 h-5 rounded-full bg-white shadow-sm ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out ${isMapVisible ? 'translate-x-6' : 'translate-x-0'}`}>
        <span
          aria-hidden="true"
          className={`absolute inset-0 flex w-full h-full items-center justify-center transition-opacity duration-200 ease-in ${isMapVisible ? 'opacity-0 duration-100 ease-out' : 'opacity-100'}`}
        >
          <MapIcon className="w-3 h-3 text-gray-400" />
        </span>
        <span
          aria-hidden="true"
          className={`absolute inset-0 flex w-full h-full items-center justify-center transition-opacity duration-100 ease-out ${isMapVisible ? 'opacity-100 duration-200 ease-in' : 'opacity-0'}`}
        >
          <MapIcon className="w-3 h-3 text-green-600" />
        </span>
      </span>
      <input
        type="checkbox"
        checked={isMapVisible}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
    </label>
  );
};

// Conditional Formatting Button
const ConditionalFormattingButton = ({ onOpenDropdown, rulesCount }) => {
  return (
    <button
      onClick={onOpenDropdown}
      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
    >
      <PaintBrushIcon className="h-4 w-4 mr-1" />
      Formatting
      {rulesCount > 0 && (
        <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
          {rulesCount}
        </span>
      )}
    </button>
  );
};

// Simplified Conditional Formatting Dropdown
const ConditionalFormattingDropdown = ({ isOpen, position, onClose, onAddRule, buttonRef, activeRules, onRemoveRule }) => {
  const [selectedColumn, setSelectedColumn] = useState('');
  const [threshold, setThreshold] = useState('');
  const dropdownRef = useRef(null);

  const dropdownPosition = useMemo(() => {
    if (!isOpen || !buttonRef.current) {
      return { top: 0, left: 0, width: 400 };
    }
    
    const rect = buttonRef.current.getBoundingClientRect();
    return {
      top: rect.bottom,
      left: rect.left,
      width: Math.max(rect.width, 400)
    };
  }, [isOpen, buttonRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isDropdownPortalClick = event.target.closest('[data-dropdown-portal="true"]') || 
                                   event.target.hasAttribute('data-dropdown-option');
      
      if (isOpen &&
          !isDropdownPortalClick &&
          dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const getNumberColumns = () => {
    return columns.filter(col => col.type === 'number').map(col => ({
      value: col.key,
      label: col.label
    }));
  };

  const numberColumnOptions = getNumberColumns();

  const handleAddRule = () => {
    if (selectedColumn && threshold !== '') {
      const newRule = {
        id: Date.now() + Math.random(),
        column: selectedColumn,
        threshold: parseFloat(threshold),
        type: 'threshold',
        enabled: true
      };
      
      onAddRule(newRule);
      
      setSelectedColumn('');
      setThreshold('');
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedColumn('');
    setThreshold('');
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl ring-1 ring-black ring-opacity-5"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Add Formatting Rule</h3>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-200 transition-colors"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Active Rules */}
      {activeRules.length > 0 && (
        <div className="px-4 py-2 border-b border-gray-200 bg-gray-25">
          <div className="text-xs font-medium text-gray-700 mb-2">Active Rules:</div>
          <div className="space-y-1">
            {activeRules.map((rule) => (
              <div key={rule.id} className="flex items-center justify-between text-xs bg-white rounded px-2 py-1 border">
                <div className="flex items-center space-x-2">
                  <span className="font-medium">{columns.find(col => col.key === rule.column)?.label}</span>
                  <span className="text-gray-500">≥ {rule.threshold}</span>
                  <div className="flex items-center space-x-1">
                    <div 
                      className="px-1 py-0.5 rounded text-xs"
                      style={{ backgroundColor: PASS_COLOR.bg, color: PASS_COLOR.text }}
                    >
                      Pass
                    </div>
                    <div 
                      className="px-1 py-0.5 rounded text-xs"
                      style={{ backgroundColor: FAIL_COLOR.bg, color: FAIL_COLOR.text }}
                    >
                      Fail
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => onRemoveRule(rule.id)}
                  className="text-red-400 hover:text-red-600"
                  title="Remove rule"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form */}
      <div className="p-4">
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Column</label>
            <CustomDropdown
              value={selectedColumn}
              onChange={(value) => {
                setSelectedColumn(value);
                setThreshold('');
              }}
              options={numberColumnOptions}
              placeholder="Select column"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Threshold (Pass if ≥)</label>
            <input
              type="number"
              step="0.1"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="e.g., 92"
              disabled={!selectedColumn}
            />
          </div>
        </div>

        {selectedColumn && threshold && (
          <div className="mb-4 p-2 bg-gray-50 rounded border">
            <div className="text-xs text-gray-600 mb-1">Preview:</div>
            <div className="flex items-center space-x-2">
              <div 
                className="px-2 py-1 rounded text-xs font-medium"
                style={{ backgroundColor: PASS_COLOR.bg, color: PASS_COLOR.text }}
              >
                ≥ {threshold} (Pass)
              </div>
              <div 
                className="px-2 py-1 rounded text-xs font-medium"
                style={{ backgroundColor: FAIL_COLOR.bg, color: FAIL_COLOR.text }}
              >
                &lt; {threshold} (Fail)
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-2 pt-3 border-t border-gray-200">
          <button
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            Cancel
          </button>
          <button
            onClick={handleAddRule}
            disabled={!selectedColumn || threshold === ''}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Rule
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Custom Tooltip Component
const Tooltip = ({ children, content }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const showTooltip = (e) => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX + rect.width / 2
      });
    }
    setIsVisible(true);
  };

  const hideTooltip = () => {
    setIsVisible(false);
  };

  const tooltip = isVisible ? (
    <div
      className="fixed z-[10000] px-2 py-1 text-xs text-white bg-gray-800 rounded shadow-lg pointer-events-none transform -translate-x-1/2"
      style={{
        top: position.top,
        left: position.left,
        maxWidth: '200px'
      }}
    >
      {content}
      <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45"></div>
    </div>
  ) : null;

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        className="cursor-help"
      >
        {children}
      </span>
      {tooltip && createPortal(tooltip, document.body)}
    </>
  );
};

// Custom Dropdown Component with Portal
const CustomDropdown = ({ value, onChange, options, placeholder, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const dropdown = isOpen && !disabled ? (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white border border-gray-200 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 max-h-60 overflow-auto"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width
      }}
      data-dropdown-portal="true"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => handleOptionClick(option.value)}
          className="w-full px-3 py-2.5 text-left text-sm text-gray-900 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 first:rounded-t-md last:rounded-b-md transition-colors"
          data-dropdown-option="true"
        >
          {option.label}
        </button>
      ))}
    </div>
  ) : null;

  return (
    <>
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={handleToggle}
          className={`w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-left focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400 cursor-pointer'
          }`}
          disabled={disabled}
        >
          <div className="flex items-center justify-between">
            <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </button>
      </div>
      
      {dropdown && createPortal(dropdown, document.body)}
    </>
  );
};

// Updated Filter Dropdown Component with project support
const FilterDropdown = ({ isOpen, position: initialPosition, onClose, onAddFilters, buttonRef, activeFilters, onRemoveFilter, uniqueProjects }) => {
  const [selectedColumn, setSelectedColumn] = useState('');
  const [selectedOperator, setSelectedOperator] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const dropdownRef = useRef(null);

  const dropdownPosition = useMemo(() => {
    if (!isOpen || !buttonRef.current) {
      return { top: 0, left: 0, width: 400 };
    }
    
    const rect = buttonRef.current.getBoundingClientRect();
    return {
      top: rect.bottom,
      left: rect.left,
      width: Math.max(rect.width, 500)
    };
  }, [isOpen, buttonRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isDropdownPortalClick = event.target.closest('[data-dropdown-portal="true"]') || 
                                   event.target.hasAttribute('data-dropdown-option');
      
      if (isOpen &&
          !isDropdownPortalClick &&
          dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const getColumnDisplayText = (columnKey) => {
    const columnMap = {
      'project_display_name': 'Project',
      'operator_name': 'Uploaded by',
      'Operator': 'Operator',
      'Density': 'Density',
      'Compaction': 'Compaction',
      'SurfaceTemp': 'Surface Temp',
      'Voids': 'Voids',
      'LocalTimeOfMeasurement': 'Local Time',
      'Station': 'Station',
      'MixName': 'Mix Name',
      'gauge_type': 'Gauge Type',
      'full_description': 'Description',
      'full_location': 'Location',
      'Description1': 'Description 1',
      'Description2': 'Description 2',
      'Location1': 'Location 1',
      'Location2': 'Location 2',
      'first_name': 'First Name',
      'last_name': 'Last Name'
    };
    return columnMap[columnKey] || columnKey;
  };

  const getOperatorDisplayText = (operator) => {
    const operatorMap = {
      'eq': '=',
      'neq': '≠',
      'ilike': 'contains',
      'not.ilike': 'does not contain',
      'gt': '>',
      'gte': '≥',
      'lt': '<',
      'lte': '≤',
      'is': 'is empty',
      'not.is': 'is not empty',
      'in': 'is one of',
      'not.in': 'is not one of'
    };
    return operatorMap[operator] || operator;
  };

  const getFilterableColumns = () => {
    const allColumns = [];
    
    columns.forEach(col => {
      if (col.filterable) {
        allColumns.push({ 
          key: col.key, 
          label: col.label, 
          type: col.type
        });
        
        if (col.subColumns) {
          col.subColumns.forEach(subCol => {
            allColumns.push({ 
              key: subCol, 
              label: subCol, 
              type: 'text',
              isSubColumn: true 
            });
          });
        }
      }
    });
    
    allColumns.push(
      { key: 'first_name', label: 'First Name', type: 'text', isSubColumn: true },
      { key: 'last_name', label: 'Last Name', type: 'text', isSubColumn: true }
    );
    
    return allColumns;
  };

  const filterableColumns = getFilterableColumns();
  const selectedColumnData = filterableColumns.find(col => col.key === selectedColumn);
  const operators = selectedColumnData ? filterOperators[selectedColumnData.type] || [] : [];
  const selectedOp = operators.find(op => op.value === selectedOperator);

  const columnOptions = filterableColumns.map(col => ({
    value: col.key,
    label: col.label
  }));

  const operatorOptions = operators.map(op => ({
    value: op.value,
    label: op.label
  }));

  // Project options for dropdown
  const projectOptions = uniqueProjects.map(project => ({
    value: project,
    label: project
  }));

  const handleAddFilter = () => {
    if (selectedColumn && selectedOperator && (filterValue || !selectedOp?.needsValue)) {
      const newFilter = {
        id: Date.now() + Math.random(),
        column: selectedColumn,
        operator: selectedOperator,
        value: filterValue
      };
      
      onAddFilters([newFilter]);
      
      setSelectedColumn('');
      setSelectedOperator('');
      setFilterValue('');
      
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedColumn('');
    setSelectedOperator('');
    setFilterValue('');
    onClose();
  };

  if (!isOpen) return null;

  const isProjectColumn = selectedColumnData?.type === 'project';

  return createPortal(
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl ring-1 ring-black ring-opacity-5"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Add Filter</h3>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-200 transition-colors"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="p-4">
        <div className="grid grid-cols-12 gap-3 mb-4">
          <div className="col-span-4">
            <label className="block text-xs font-medium text-gray-700 mb-1">Column</label>
            <CustomDropdown
              value={selectedColumn}
              onChange={(value) => {
                setSelectedColumn(value);
                setSelectedOperator(value === 'project_display_name' ? 'eq' : '');
                setFilterValue('');
              }}
              options={columnOptions}
              placeholder="Pick a column"
            />
          </div>
          <div className="col-span-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">Operator</label>
            <CustomDropdown
              value={selectedOperator}
              onChange={(value) => {
                setSelectedOperator(value);
                setFilterValue('');
              }}
              options={operatorOptions}
              placeholder="Operator"
              disabled={!selectedColumn}
            />
          </div>
          <div className="col-span-5">
            <label className="block text-xs font-medium text-gray-700 mb-1">Value</label>
            {selectedOperator && selectedOp?.needsValue ? (
              isProjectColumn ? (
                <CustomDropdown
                  value={filterValue}
                  onChange={setFilterValue}
                  options={projectOptions}
                  placeholder="Select project"
                />
              ) : selectedColumnData?.type === 'datetime' ? (
                <input
                  type="date"
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Select date"
                />
              ) : (
                <input
                  type={selectedColumnData?.type === 'number' ? 'number' : 'text'}
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Enter value"
                />
              )
            ) : (
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 text-gray-400"
                placeholder={selectedOperator && !selectedOp?.needsValue ? 'No value needed' : 'Enter value'}
                disabled
              />
            )}
          </div>
        </div>
        <div className="flex justify-end space-x-2 pt-3 border-t border-gray-200">
          <button
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            Cancel
          </button>
          <button
            onClick={handleAddFilter}
            disabled={!selectedColumn || !selectedOperator || (selectedOp?.needsValue && !filterValue)}
            className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Filter
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

  const { user } = useContext(AuthContext);
  const [measurements, setMeasurements] = useState([]);
  const [mapMeasurements, setMapMeasurements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [memberIds, setMemberIds] = useState([]);
  const [mapCenter, setMapCenter] = useState({ lat: 42.73116, lng: -73.68657 });


  
  // Table state
  const [sortBy, setSortBy] = useState('LocalTimeOfMeasurement');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [uniqueProjects, setUniqueProjects] = useState([]);
  
  // Conditional formatting state
  const [conditionalFormattingRules, setConditionalFormattingRules] = useState([]);
  const [showFormattingDropdown, setShowFormattingDropdown] = useState(false);
  const [formattingDropdownPosition, setFormattingDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  // UI state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDetailsMeasurement, setSelectedDetailsMeasurement] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showSortModal, setShowSortModal] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filterDropdownPosition, setFilterDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  // Column resizing state
  const [columnWidths, setColumnWidths] = useState(() => {
    const widths = {};
    columns.forEach(col => {
      widths[col.key] = col.width;
    });
    return widths;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  
  // Map-related state
  const [allGeofences, setAllGeofences] = useState([]);
  const [geofencePolygon, setGeofencePolygon] = useState(null);
  const [mapStyle, setMapStyle] = useState([]);
  const [isMapVisible, setIsMapVisible] = useState(true);

  const debounceTimeoutRef = useRef(null);
  const exportMenuRef = useRef(null);
  const filterButtonRef = useRef(null);
  const formattingButtonRef = useRef(null);
  const tableRef = useRef(null);

  

  // Conditional formatting logic
  const getCellStyle = useCallback((value, columnKey, measurement) => {
    const applicableRule = conditionalFormattingRules
      .find(rule => rule.enabled && rule.column === columnKey);

    if (!applicableRule || value === null || value === undefined) {
      return {};
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return {};

    const color = numValue >= applicableRule.threshold ? PASS_COLOR : FAIL_COLOR;
    return {
      backgroundColor: color.bg,
      color: color.text,
      fontWeight: '500'
    };
  }, [conditionalFormattingRules]);

  // Initialize
  useEffect(() => {
    document.title = "Measurement Data | TransTech Connect";
    return () => {
      document.title = "TransTech Connect";
    };
  }, []);

  // Column resizing handlers
  const handleMouseDown = useCallback((e, columnKey) => {
    e.preventDefault();
    setIsResizing(true);
    setResizingColumn(columnKey);
    setStartX(e.clientX);
    setStartWidth(columnWidths[columnKey]);
  }, [columnWidths]);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing || !resizingColumn) return;
    
    const diff = e.clientX - startX;
    const newWidth = Math.max(
      startWidth + diff,
      columns.find(col => col.key === resizingColumn)?.minWidth || 60
    );
    
    setColumnWidths(prev => ({
      ...prev,
      [resizingColumn]: newWidth
    }));
  }, [isResizing, resizingColumn, startX, startWidth]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    setResizingColumn(null);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Fetch organization member IDs
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!user) return;

      try {
        let tempMemberIds = [user.id];
        
        const { data: orgMember, error: orgError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();

        if (orgError && orgError.code !== 'PGRST116') {
          console.error('Error fetching organization membership:', orgError);
          return;
        }

        if (orgMember) {
          const { data: orgMembers, error: orgMembersError } = await supabase
            .from('organization_members')
            .select('user_id')
            .eq('organization_id', orgMember.organization_id);

          if (orgMembersError) {
            console.error('Error fetching organization members:', orgMembersError);
            return;
          }

          tempMemberIds = orgMembers.map((member) => member.user_id);
        }

        setMemberIds(tempMemberIds);
      } catch (err) {
        console.error('Unexpected error fetching initial data:', err);
      }
    };

    fetchInitialData();
  }, [user]);

  // Fetch unique projects for filter dropdown
  const fetchUniqueProjects = useCallback(async () => {
    if (!user || memberIds.length === 0) return;

    try {
      const { data, error } = await supabase
        .from('measurements_with_details')
        .select('project_display_name')
        .in('profile_id', memberIds)
        .not('project_display_name', 'is', null);

      if (error) throw error;

      const unique = [...new Set(data.map(item => item.project_display_name))].sort();
      setUniqueProjects(unique);
    } catch (err) {
      console.error('Error fetching unique projects:', err);
      setUniqueProjects([]);
    }
  }, [user, memberIds]);

  useEffect(() => {
    fetchUniqueProjects();
  }, [fetchUniqueProjects]);

  // Apply filter to query (enhanced for Supabase-style operators)
  const applyFilterToQuery = useCallback((query, filter) => {
    const { column, operator, value } = filter;
    
    if (column === 'operator_name') {
      switch (operator) {
        case 'ilike':
          return query.ilike(column, `%${value}%`);
        case 'not.ilike':
          return query.not.ilike(column, `%${value}%`);
        case 'eq':
          return query.eq(column, value);
        case 'neq':
          return query.neq(column, value);
        case 'is':
          return query.is(column, null);
        case 'not.is':
          return query.not.eq(column, null);
        case 'in':
          const inValues = value.split(',').map(v => v.trim()).filter(v => v.length > 0);
          return query.in(column, inValues);
        case 'not.in':
          const notInValues = value.split(',').map(v => v.trim()).filter(v => v.length > 0);
          return query.not.in(column, notInValues);
        default:
          return query;
      }
    }
    
    if (column === 'full_description') {
      switch (operator) {
        case 'ilike':
          return query.or(`Description1.ilike.%${value}%,Description2.ilike.%${value}%`);
        case 'not.ilike':
          return query.not.or(`Description1.ilike.%${value}%,Description2.ilike.%${value}%`);
        case 'eq':
          return query.or(`Description1.eq.${value},Description2.eq.${value}`);
        case 'neq':
          return query.not.or(`Description1.eq.${value},Description2.eq.${value}`);
        case 'is':
          return query.and('Description1.is.null,Description2.is.null');
        case 'not.is':
          return query.or('Description1.not.is.null,Description2.not.is.null');
        case 'in':
          const inValues = value.split(',').map(v => v.trim()).filter(v => v.length > 0);
          const inConditions = inValues.map(val => `Description1.eq.${val},Description2.eq.${val}`).join(',');
          return query.or(inConditions);
        case 'not.in':
          const notInValues = value.split(',').map(v => v.trim()).filter(v => v.length > 0);
          const notInConditions = notInValues.map(val => `Description1.eq.${val},Description2.eq.${val}`).join(',');
          return query.not.or(notInConditions);
        default:
          return query;
      }
    }
    
    if (column === 'full_location') {
      switch (operator) {
        case 'ilike':
          return query.or(`Location1.ilike.%${value}%,Location2.ilike.%${value}%`);
        case 'not.ilike':
          return query.not.or(`Location1.ilike.%${value}%,Location2.ilike.%${value}%`);
        case 'eq':
          return query.or(`Location1.eq.${value},Location2.eq.${value}`);
        case 'neq':
          return query.not.or(`Location1.eq.${value},Location2.eq.${value}`);
        case 'is':
          return query.and('Location1.is.null,Location2.is.null');
        case 'not.is':
          return query.or('Location1.not.is.null,Location2.not.is.null');
        case 'in':
          const inValues = value.split(',').map(v => v.trim()).filter(v => v.length > 0);
          const inConditions = inValues.map(val => `Location1.eq.${val},Location2.eq.${val}`).join(',');
          return query.or(inConditions);
        case 'not.in':
          const notInValues = value.split(',').map(v => v.trim()).filter(v => v.length > 0);
          const notInConditions = notInValues.map(val => `Location1.eq.${val},Location2.eq.${val}`).join(',');
          return query.not.or(notInConditions);
        default:
          return query;
      }
    }
    
    switch (operator) {
      case 'eq':
        return query.eq(column, value);
      case 'neq':
        return query.neq(column, value);
      case 'ilike':
        return query.ilike(column, `%${value}%`);
      case 'not.ilike':
        return query.not.ilike(column, `%${value}%`);
      case 'gt':
        return query.gt(column, value);
      case 'gte':
        return query.gte(column, value);
      case 'lt':
        return query.lt(column, value);
      case 'lte':
        return query.lte(column, value);
      case 'is':
        return query.is(column, null);
      case 'not.is':
        return query.not.eq(column, null);
      case 'in':
        const inValues = value.split(',').map(v => v.trim()).filter(v => v.length > 0);
        return query.in(column, inValues);
      case 'not.in':
        const notInValues = value.split(',').map(v => v.trim()).filter(v => v.length > 0);
        return query.not.in(column, notInValues);
      default:
        return query;
    }
  }, []);

  // Fetch measurements with filters, sorting, and pagination
  const fetchMeasurements = useCallback(async (page = 1) => {
    if (!user || memberIds.length === 0) return;

    setLoading(true);
    try {
      const from = (page - 1) * rowsPerPage;
      const to = from + rowsPerPage - 1;

      let baseQuery = supabase
        .from('measurements_with_details')
        .select('*', { count: 'exact' })
        .in('profile_id', memberIds)
        .limit(MAX_RECORDS);

      filters.forEach(filter => {
        baseQuery = applyFilterToQuery(baseQuery, filter);
      });

      if (searchTerm) {
        baseQuery = baseQuery.or(`project_display_name.ilike.%${searchTerm}%,operator_name.ilike.%${searchTerm}%,Operator.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,Description1.ilike.%${searchTerm}%,Description2.ilike.%${searchTerm}%,Location1.ilike.%${searchTerm}%,Location2.ilike.%${searchTerm}%,Station.ilike.%${searchTerm}%,MixName.ilike.%${searchTerm}%,gauge_type.ilike.%${searchTerm}%`);
      }

      baseQuery = baseQuery.order(sortBy, { ascending: sortOrder === 'asc' });

      const { data: tableData, count, error: tableError } = await baseQuery.range(from, to);

      if (tableError) throw tableError;

      setMeasurements(tableData || []);
      setTotalRecords(count || 0);

      let mapQuery = supabase
        .from('measurements_with_details')
        .select('id, latitude, longitude, Density, Compaction, SurfaceTemp, operator_name, project_display_name, project_id, LocalTimeOfMeasurement, Description1, Description2, Location1, Location2')
        .in('profile_id', memberIds)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null)
        .limit(MAX_MAP_MARKERS);

      filters.forEach(filter => {
        mapQuery = applyFilterToQuery(mapQuery, filter);
      });

      if (searchTerm) {
        mapQuery = mapQuery.or(`project_display_name.ilike.%${searchTerm}%,operator_name.ilike.%${searchTerm}%,Operator.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,Description1.ilike.%${searchTerm}%,Description2.ilike.%${searchTerm}%,Location1.ilike.%${searchTerm}%,Location2.ilike.%${searchTerm}%,Station.ilike.%${searchTerm}%,MixName.ilike.%${searchTerm}%,gauge_type.ilike.%${searchTerm}%`);
      }

      mapQuery = mapQuery.order(sortBy, { ascending: sortOrder === 'asc' });

      const { data: mapData, error: mapError } = await mapQuery;

      if (mapError) throw mapError;

      setMapMeasurements(mapData || []);

      if (mapData && mapData.length > 0 && mapData[0].latitude && mapData[0].longitude) {
        setMapCenter({ lat: mapData[0].latitude, lng: mapData[0].longitude });
      }

    } catch (err) {
      console.error('Error fetching measurements:', err);
      setMeasurements([]);
      setMapMeasurements([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [user, memberIds, filters, searchTerm, sortBy, sortOrder, rowsPerPage, applyFilterToQuery]);

  // Debounced fetch on filter/search changes
  useEffect(() => {
    if (!user || memberIds.length === 0) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchMeasurements(1);
    }, 500);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [fetchMeasurements]);



// Fetch on page change (separate from filter/search changes)
useEffect(() => {
  if (!user || memberIds.length === 0) return;
  
  // Only fetch if we're not on page 1 (page 1 is handled by the debounced effect)
  if (currentPage > 1) {
    fetchMeasurements(currentPage);
  }
}, [currentPage, user, memberIds, fetchMeasurements]);

  // Handle sorting
  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnKey);
      setSortOrder('desc');
    }
  };

  // Handle row selection
  const toggleRowSelection = (measurement) => {
    setSelectedRows(prev => {
      if (prev.find(row => row.id === measurement.id)) {
        return prev.filter(row => row.id !== measurement.id);
      } else {
        return [...prev, measurement];
      }
    });
  };

  const toggleAllSelection = () => {
    if (allSelected) {
      setSelectedRows([]);
      setAllSelected(false);
    } else {
      setSelectedRows([...measurements]);
      setAllSelected(true);
    }
  };

  // Filter management
  const addMultipleFilters = (newFilters) => {
    setFilters(prev => [...prev, ...newFilters]);
  };

  const removeFilter = (filterId) => {
    setFilters(prev => prev.filter(f => f.id !== filterId));
  };

  const clearAllFilters = () => {
    setFilters([]);
  };

  // Conditional formatting management
  const handleAddFormattingRule = (rule) => {
    // Remove any existing rule for the same column
    setConditionalFormattingRules(prev => [
      ...prev.filter(r => r.column !== rule.column),
      rule
    ]);
  };

  const handleRemoveFormattingRule = (ruleId) => {
    setConditionalFormattingRules(prev => prev.filter(r => r.id !== ruleId));
  };

  // Handle formatting dropdown positioning
  const handleFormattingButtonClick = () => {
    if (!showFormattingDropdown && formattingButtonRef.current) {
      const rect = formattingButtonRef.current.getBoundingClientRect();
      setFormattingDropdownPosition({
        top: rect.bottom,
        left: rect.left,
        width: Math.max(rect.width, 400)
      });
    }
    setShowFormattingDropdown(!showFormattingDropdown);
  };

  // Helper functions
  const getColumnDisplayText = (columnKey) => {
    const columnMap = {
      'project_display_name': 'Project',
      'operator_name': 'Uploaded by',
      'Operator': 'Operator',
      'Density': 'Density',
      'Compaction': 'Compaction',
      'SurfaceTemp': 'Surface Temp',
      'Voids': 'Voids',
      'LocalTimeOfMeasurement': 'Local Time',
      'Station': 'Station',
      'MixName': 'Mix Name',
      'gauge_type': 'Gauge Type',
      'full_description': 'Description',
      'full_location': 'Location',
      'latitude': 'Latitude',
      'longitude': 'Longitude',
      'Description1': 'Description 1',
      'Description2': 'Description 2',
      'Location1': 'Location 1',
      'Location2': 'Location 2',
      'first_name': 'First Name',
      'last_name': 'Last Name'
    };
    return columnMap[columnKey] || columnKey;
  };

  const getOperatorDisplayText = (operator) => {
    const operatorMap = {
      'eq': '=',
      'neq': '≠',
      'ilike': 'contains',
      'not.ilike': 'does not contain',
      'gt': '>',
      'gte': '≥',
      'lt': '<',
      'lte': '≤',
      'is': 'is empty',
      'not.is': 'is not empty',
      'in': 'is one of',
      'not.in': 'is not one of'
    };
    
    if (operator === 'eq') return 'on';
    if (operator === 'gt') return 'after';
    if (operator === 'lt') return 'before';
    
    return operatorMap[operator] || operator;
  };

  // Export functionality
  const exportToCSV = (data, filename = "measurements.csv") => {
    if (!data || data.length === 0) return;

    const headers = columns.map(col => col.label);
    const rows = data.map(row =>
      columns.map(col => {
        let value = row[col.key];
        if (col.format && value !== null && value !== undefined) {
          value = col.format(value, row);
        }
        if (typeof value === "string") {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value !== null && value !== undefined ? value : "";
      }).join(",")
    );

    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Action handlers
  const handleViewDetails = (measurement) => {
    setSelectedDetailsMeasurement(measurement);
    setShowDetailsModal(true);
    setActiveDropdown(null);
  };

const handleViewOnMap = (measurement) => {
  // Ensure map is visible
  if (!isMapVisible) {
    setIsMapVisible(true);
  }
  
  // Set selected measurement for highlighting
  setSelectedMapMeasurement(measurement.id);
  
  // Center map on the measurement if coordinates exist
  if (measurement.latitude && measurement.longitude) {
    setMapCenter({ 
      lat: parseFloat(measurement.latitude), 
      lng: parseFloat(measurement.longitude) 
    });
  }
  
  // Scroll to map section smoothly
  setTimeout(() => {
    const mapSection = document.querySelector('.h-\\[40vh\\]');
    if (mapSection) {
      mapSection.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, 100);
  
  setActiveDropdown(null);
};

  const handleOpenProject = async (measurementId) => {
    try {
      const measurement = measurements.find(m => m.id === measurementId);
      if (measurement && measurement.project_id) {
        window.open(`https://transtechconnect.com/projects/${measurement.project_id}`, '_blank');
        return;
      }

      const { data, error } = await supabase
        .from('project_mapping')
        .select('project_id')
        .eq('measurement_id', measurementId)
        .single();

      if (error || !data) {
        alert('No project found for this measurement.');
        return;
      }

      window.open(`https://transtechconnect.com/projects/${data.project_id}`, '_blank');
    } catch (err) {
      console.error('Error opening project:', err);
      alert('Error opening project.');
    }
  };

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle filter dropdown positioning
  const handleFilterButtonClick = () => {
    if (!showFilterDropdown && filterButtonRef.current) {
      const rect = filterButtonRef.current.getBoundingClientRect();
      setFilterDropdownPosition({
        top: rect.bottom,
        left: rect.left,
        width: Math.max(rect.width, 500)
      });
    }
    setShowFilterDropdown(!showFilterDropdown);
  };

  // Fetch all geofences
  const fetchAllGeofences = useCallback(async () => {
    if (!user || memberIds.length === 0) {
      setAllGeofences([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('measurements_with_details')
        .select('project_id, project_display_name, project_geofence')
        .in('profile_id', memberIds)
        .not('project_geofence', 'is', null)
        .not('project_id', 'is', null);

      if (error) throw error;

      const uniqueGeofences = [];
      const seenProjects = new Set();

      data?.forEach(item => {
        if (!seenProjects.has(item.project_id) && item.project_geofence) {
          seenProjects.add(item.project_id);
          
          try {
            const geoJson = typeof item.project_geofence === 'string' 
              ? JSON.parse(item.project_geofence) 
              : item.project_geofence;

            if (geoJson && geoJson.coordinates && geoJson.coordinates.length > 0) {
              const coords = geoJson.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
              
              const colors = [
                { fill: "#3B82F6", stroke: "#1D4ED8" },
                { fill: "#10B981", stroke: "#047857" },
                { fill: "#F59E0B", stroke: "#D97706" },
                { fill: "#EF4444", stroke: "#DC2626" },
                { fill: "#8B5CF6", stroke: "#7C3AED" },
                { fill: "#06B6D4", stroke: "#0891B2" },
                { fill: "#F97316", stroke: "#EA580C" },
                { fill: "#EC4899", stroke: "#DB2777" },
              ];
              
              const colorIndex = uniqueGeofences.length % colors.length;
              const projectColor = colors[colorIndex];

              uniqueGeofences.push({
                id: item.project_id,
                name: item.project_display_name,
                coordinates: coords,
                options: {
                  fillColor: projectColor.fill,
                  fillOpacity: 0.2,
                  strokeColor: projectColor.stroke,
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                }
              });
            }
          } catch (parseError) {
            console.warn('Error parsing geofence for project:', item.project_display_name, parseError);
          }
        }
      });

      setAllGeofences(uniqueGeofences);
    } catch (err) {
      console.error('Error fetching all geofences:', err);
      setAllGeofences([]);
    }
  }, [user, memberIds]);

  useEffect(() => {
    fetchAllGeofences();
  }, [fetchAllGeofences]);

  // Fetch specific geofence for selected project filter
  useEffect(() => {
    const fetchSpecificGeofence = async () => {
      const projectFilter = filters.find(f => f.column === 'project_display_name');
      if (!projectFilter || projectFilter.value === 'No Project') {
        setGeofencePolygon(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('measurements_with_details')
          .select('project_geofence')
          .in('profile_id', memberIds)
          .eq('project_display_name', projectFilter.value)
          .not('project_geofence', 'is', null)
          .limit(1)
          .single();

        if (error || !data?.project_geofence) {
          setGeofencePolygon(null);
          return;
        }

        const geoJson = typeof data.project_geofence === 'string' 
          ? JSON.parse(data.project_geofence) 
          : data.project_geofence;

        if (geoJson && geoJson.coordinates && geoJson.coordinates.length > 0) {
          const coords = geoJson.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
          setGeofencePolygon(coords);
        } else {
          setGeofencePolygon(null);
        }
      } catch (err) {
        console.error('Error fetching specific geofence:', err);
        setGeofencePolygon(null);
      }
    };

    fetchSpecificGeofence();
  }, [filters, memberIds]);

  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  // Sort Modal Component
const SortModal = ({ onClose, currentSort, onApplySort }) => {
  const [selectedColumn, setSelectedColumn] = useState(currentSort.column || '');
  const [sortOrder, setSortOrder] = useState(currentSort.order || 'desc');

  const sortableColumns = columns.filter(col => col.sortable);
  
  const columnOptions = sortableColumns.map(col => ({
    value: col.key,
    label: col.label
  }));

  const handleSubmit = () => {
    if (selectedColumn) {
      onApplySort(selectedColumn, sortOrder);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-0 overflow-hidden border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">Sort by</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-200 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {currentSort.column && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                <div className="flex items-center space-x-2">
                  <Bars3BottomLeftIcon className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium">sort by</span>
                  <span className="text-sm text-gray-700">{currentSort.column}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 mr-2">{currentSort.order}ending:</span>
                  <div className={`w-8 h-5 rounded-full p-1 ${currentSort.order === 'asc' ? 'bg-green-500' : 'bg-gray-300'}`}>
                    <div className={`w-3 h-3 rounded-full bg-white transition-transform ${currentSort.order === 'asc' ? 'translate-x-3' : 'translate-x-0'}`}></div>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="ml-2 text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Pick a column to sort by</label>
              <CustomDropdown
                value={selectedColumn}
                onChange={setSelectedColumn}
                options={columnOptions}
                placeholder="Select column"
              />
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Ascending</span>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={sortOrder === 'asc'}
                  onChange={(e) => setSortOrder(e.target.checked ? 'asc' : 'desc')}
                  className="sr-only"
                />
                <div className={`w-11 h-6 rounded-full p-1 transition-colors ${sortOrder === 'asc' ? 'bg-green-500' : 'bg-gray-300'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white transition-transform ${sortOrder === 'asc' ? 'translate-x-5' : 'translate-x-0'}`}></div>
                </div>
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!selectedColumn}
                className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply sorting
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Details Modal Component
const DetailsModal = ({ measurement, onClose }) => {
  const formatValue = (value, type = 'text') => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-gray-400 italic">No data</span>;
    }
    
    if (type === 'datetime') {
      return new Date(value).toLocaleString();
    }
    
    if (type === 'number') {
      return parseFloat(value).toFixed(1);
    }
    
    return value;
  };

  const MeasurementCard = ({ label, value, unit = '' }) => {
    const numValue = parseFloat(value);
    const isNumeric = !isNaN(numValue) && value !== null && value !== undefined && value !== '';
    
    return (
      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <div className="text-xs font-medium text-gray-600 mb-1">{label}</div>
        <div className="text-lg font-bold text-gray-900">
          {isNumeric ? numValue.toFixed(1) : <span className="text-gray-400 text-sm italic">No data</span>}
          {isNumeric && unit && <span className="text-xs font-normal text-gray-500 ml-1">{unit}</span>}
        </div>
      </div>
    );
  };

  const InfoItem = ({ label, value, type = 'text' }) => (
    <div className="flex justify-between items-center py-1">
      <span className="text-xs text-gray-600 font-medium">{label}</span>
      <span className="text-xs text-gray-900 font-mono text-right ml-2">
        {formatValue(value, type)}
      </span>
    </div>
  );

  const Section = ({ title, icon: Icon, children, className = "" }) => (
    <div className={`bg-white rounded-lg border border-gray-200 overflow-hidden ${className}`}>
      <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <Icon className="h-4 w-4 text-gray-600" />
          <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">{title}</h3>
        </div>
      </div>
      <div className="p-3">
        {children}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm p-4 ">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-auto max-h-[90vh] overflow-hidden border border-gray-200 md:h-auto">
        {/* Header */}
        <div className="bg-black px-4 py-3 text-white overflow-hidden">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-bold">Measurement Details</h2>
              <p className="text-blue-100 text-sm">
                {measurement.project_display_name && (
                  <>Project: {measurement.project_display_name}</>
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-full transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable on mobile, contained on desktop */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)] md:overflow-visible md:max-h-none">
          {/* Top Section: Measurements */}
          <div className="mb-4">
            <Section title="Measurements" icon={ChartBarIcon}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <MeasurementCard 
                  label="Compaction" 
                  value={measurement.Compaction} 
                  unit="%" 
                />
                <MeasurementCard 
                  label="Surface Temp" 
                  value={measurement.SurfaceTemp} 
                />
                <MeasurementCard 
                  label="Density" 
                  value={measurement.Density} 
                  unit="" 
                />
                <MeasurementCard 
                  label="Voids" 
                  value={measurement.Voids} 
                  unit="%"
                />
              </div>
            </Section>
          </div>

          {/* Middle Section: Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {/* Basic Information */}
            <Section title="Basic Information" icon={ClockIcon}>
              <div className="space-y-1">
                <InfoItem 
                  label="Time" 
                  value={measurement.LocalTimeOfMeasurement} 
                  type="datetime" 
                />
                <InfoItem label="Station" value={measurement.Station} />
                <InfoItem label="Mix Name" value={measurement.MixName} />
                <InfoItem label="Gauge Type" value={measurement.gauge_type} />
              </div>
            </Section>

            {/* Personnel */}
            <Section title="Personnel" icon={UserIcon}>
              <div className="space-y-1">
                <InfoItem label="Operator" value={measurement.Operator} />
                <InfoItem label="Uploaded by" value={measurement.operator_name} />
                <InfoItem label="First Name" value={measurement.first_name} />
                <InfoItem label="Last Name" value={measurement.last_name} />
              </div>
            </Section>
          </div>

          {/* Bottom Section: Two Column Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Location */}
            <Section title="Location" icon={MapPinIcon}>
              <div className="space-y-1">
                <InfoItem label="Location 1" value={measurement.Location1} />
                <InfoItem label="Location 2" value={measurement.Location2} />
                <InfoItem label="Coordinates" value={`${measurement.latitude}, ${measurement.longitude}`} />
              </div>
            </Section>

            {/* Description */}
            <Section title="Description" icon={BeakerIcon}>
              <div className="space-y-1">
                <InfoItem label="Description 1" value={measurement.Description1} />
                <InfoItem label="Description 2" value={measurement.Description2} />
                <InfoItem label="Contact" value={measurement.Contact} />
              </div>
            </Section>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add this useEffect to clear selection when filters change
useEffect(() => {
  setSelectedMapMeasurement(null);
}, [filters, searchTerm]);

  return (
    <Layout noPadding={true} noScroll={true}>
      <div className="h-full flex flex-col p-2">
        {/* Map Section - Fixed height when visible */}
        <div className={`flex-shrink-0 transition-all duration-300 ease-in-out ${isMapVisible ? 'h-[40vh]' : 'h-0'} overflow-hidden w-full`}>
          <div className="h-full rounded-lg border border-gray-200 overflow-hidden">
            <MeasurementsMap
  measurements={mapMeasurements}
  selectedProject={filters.find(f => f.column === 'project_display_name')?.value || ''}
  selectedMeasurement={selectedMapMeasurement}
  allGeofences={allGeofences}
  geofencePolygon={geofencePolygon}
  onOpenProject={handleOpenProject}
  mapStyle={mapStyle}
  setMapStyle={setMapStyle}
/>
          </div>
        </div>

        {/* Controls Section - Fixed height */}
        <div className="flex-shrink-0 py-3">
          {/* Active filters row */}
          {filters.length > 0 && (
            <div className="flex items-center space-x-2 flex-wrap gap-2 pb-2">
              <span className="text-sm text-gray-500">Filters:</span>
              {filters.map((filter) => {
                const operatorText = getOperatorDisplayText(filter.operator);
                const showValue = filter.value && !['is', 'not.is'].includes(filter.operator);
                
                return (
                  <div
                    key={filter.id}
                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-50 text-green-700 border border-green-200 shadow-sm"
                  >
                    <span className="font-semibold text-green-800">{getColumnDisplayText(filter.column)}</span>
                    <span className="mx-1 text-green-600">{operatorText}</span>
                    {showValue && <span className="text-green-700">"{filter.value}"</span>}
                    <button
                      onClick={() => removeFilter(filter.id)}
                      className="ml-1 inline-flex items-center justify-center w-3 h-3 rounded-full text-green-500 hover:bg-green-200 hover:text-green-700 transition-colors"
                    >
                      <XMarkIcon className="h-2.5 w-2.5" />
                    </button>
                  </div>
                );
              })}
              <button
                onClick={clearAllFilters}
                className="text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 px-2 py-1 rounded-md transition-colors"
              >
                Clear all
              </button>
            </div>
          )}

          {/* Main controls row */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search..."
                className="block w-64 pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Filter Button */}
            <button
              ref={filterButtonRef}
              onClick={handleFilterButtonClick}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <FunnelIcon className="h-4 w-4 mr-1" />
              Filter
              {filters.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                  {filters.length}
                </span>
              )}
            </button>

            {/* Conditional Formatting Button */}
            <button
              ref={formattingButtonRef}
              onClick={handleFormattingButtonClick}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <PaintBrushIcon className="h-4 w-4 mr-1" />
              Formatting
              {conditionalFormattingRules.filter(r => r.enabled).length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                  {conditionalFormattingRules.filter(r => r.enabled).length}
                </span>
              )}
            </button>

            {/* Spacer to push right-side controls */}
            <div className="flex-1" />

            {/* Map Toggle */}
            <MapToggle 
              isMapVisible={isMapVisible}
              onChange={setIsMapVisible}
            />

            {/* Export */}
            <div className="relative" ref={exportMenuRef}>
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={selectedRows.length === 0}
                className={`inline-flex items-center px-3 py-2 border shadow-sm text-sm leading-4 font-medium rounded-md ${
                  selectedRows.length > 0
                    ? 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100'
                    : 'border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed'
                }`}
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                Export ({selectedRows.length})
              </button>
              {showExportMenu && selectedRows.length > 0 && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50" style={{ top: '100%' }}>
                  <button
                    onClick={() => {
                      exportToCSV(selectedRows, "selected_measurements.csv");
                      setShowExportMenu(false);
                      setSelectedRows([]);
                      setAllSelected(false);
                    }}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Export Selected
                  </button>
                  <button
                    onClick={() => {
                      exportToCSV(measurements, "all_measurements.csv");
                      setShowExportMenu(false);
                    }}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Export All
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Performance warning */}
          {totalRecords > 10000 && (
            <div className="mt-1 p-1.5 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-xs text-amber-700">
                <strong>Large dataset:</strong> {totalRecords.toLocaleString()} records. Consider using filters for better performance.
              </p>
            </div>
          )}
        </div>

        {/* Table Section - Takes remaining height */}
        <div className="flex-1 min-h-0 flex flex-col w-full">
          <div className="h-full bg-white shadow border border-gray-200 rounded-lg flex flex-col w-full">
            {/* Table content - scrollable */}
            <div className="flex-1 overflow-auto">
              <table className="min-w-full table-fixed border-collapse">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-20 shadow-sm">
                  <tr className="rounded-t-lg">
                    <th className="w-12 px-3 py-2 border-r border-gray-200 bg-gray-50 rounded-tl-lg">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        checked={allSelected && measurements.length > 0}
                        onChange={toggleAllSelection}
                      />
                    </th>
                    <th className="w-28 px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 rounded-tr-lg border-r border-gray-200">
                      Actions
                    </th>
                    {columns.map((column, index) => (
                      <th
                        key={column.key}
                        className="relative px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 bg-gray-50"
                        style={{ width: `${columnWidths[column.key]}px` }}
                      >
                        <div 
                          className={`flex items-center space-x-1 ${
                            column.sortable ? 'cursor-pointer hover:bg-gray-100 rounded px-1 py-0.5' : ''
                          }`}
                          onClick={() => column.sortable && handleSort(column.key)}
                        >
                          <span className="truncate">{column.label}</span>
                          {column.subColumns && (
                            <Tooltip content={`Combines ${column.key === 'full_description' ? 'Description1 and Description2' : 'Location1 and Location2'} fields`}>
                              <span className="text-xs text-gray-400">
                                <InformationCircleIcon className="h-3 w-3" />
                              </span>
                            </Tooltip>
                          )}
                          {column.sortable && sortBy === column.key && (
                            <div className="flex items-center">
                              {sortOrder === 'asc' ? (
                                <ChevronUpIcon className="h-3 w-3 text-gray-700" />
                              ) : (
                                <ChevronDownIcon className="h-3 w-3 text-gray-700" />
                              )}
                            </div>
                          )}
                        </div>
                        <div
                          className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-green-500 hover:w-1.5 transition-all"
                          onMouseDown={(e) => handleMouseDown(e, column.key)}
                          style={{ backgroundColor: isResizing && resizingColumn === column.key ? '#10b981' : 'transparent' }}
                        />
                      </th>
                    ))}
                    
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {loading ? (
                    <tr>
                      <td colSpan={columns.length + 2} className="px-6 py-12 text-center border-b border-gray-100">
                        <div className="flex items-center justify-center space-x-2">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                          <span className="text-sm text-gray-500">Loading measurements...</span>
                        </div>
                      </td>
                    </tr>
                  ) : measurements.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length + 2} className="px-6 py-12 text-center border-b border-gray-100">
                        <div className="text-sm text-gray-500">
                          No measurements found. Try adjusting your filters or search terms.
                        </div>
                      </td>
                    </tr>
                  ) : (
                    measurements.map((measurement, rowIndex) => (
                      <tr
                        key={measurement.id}
                        className={`hover:bg-blue-50 transition-colors border-b border-gray-100 ${
                          selectedRows.find(row => row.id === measurement.id) ? 'bg-green-50' : rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        }`}
                      >
                        <td className="w-12 px-3 py-1 border-r border-gray-200">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                            checked={!!selectedRows.find(row => row.id === measurement.id)}
                            onChange={() => toggleRowSelection(measurement)}
                          />
                        </td>
                        <td className="w-28 px-3 py-1 text-center border-r border-gray-200">
  <span className="isolate inline-flex rounded-md shadow-xs">
    <button
  type="button"
  onClick={() => handleViewOnMap(measurement)}
  title="View on Map"
  disabled={!measurement.latitude || !measurement.longitude}
  className={`relative inline-flex items-center justify-center rounded-l-md bg-white p-1.5 text-sm font-semibold ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus:z-10 ${
    !measurement.latitude || !measurement.longitude 
      ? 'text-gray-300 cursor-not-allowed' 
      : 'text-gray-400 hover:text-gray-600'
  }`}
>
  <MapPinIcon className="h-3.5 w-3.5" />
</button>
    <button
      type="button"
      onClick={() => handleViewDetails(measurement)}
      title="View Details"
      className="relative -ml-px inline-flex items-center justify-center bg-white p-1.5 text-sm font-semibold text-gray-400 ring-1 ring-gray-300 ring-inset hover:bg-gray-50 hover:text-gray-600 focus:z-10"
    >
      <EyeIconSolid className="h-3.5 w-3.5" />
    </button>
    <button
      type="button"
      onClick={() => {
        handleOpenProject(measurement.id);
      }}
      title="Open Project"
      className="relative -ml-px inline-flex items-center justify-center rounded-r-md bg-white p-1.5 text-sm font-semibold text-gray-400 ring-1 ring-gray-300 ring-inset hover:bg-gray-50 hover:text-gray-600 focus:z-10"
    >
      <FolderOpenIcon className="h-3.5 w-3.5" />
    </button>
  </span>
</td>
                        {columns.map((column) => (
                          <td 
                            key={column.key} 
                            className="px-3 py-1 text-xs text-gray-900 border-r border-gray-200"
                            style={{ 
                              width: `${columnWidths[column.key]}px`,
                              ...getCellStyle(measurement[column.key], column.key, measurement)
                            }}
                          >
                            <div className="truncate" title={column.format ? column.format(measurement[column.key], measurement) : (measurement[column.key] || '')}>
                              {column.format ? column.format(measurement[column.key], measurement) : (measurement[column.key] || '')}
                            </div>
                          </td>
                        ))}
                        
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Compact Footer - Fixed */}
            <div className="flex-shrink-0 bg-gray-50 px-4 py-1 border-t border-gray-200 rounded-b-lg flex items-center justify-between text-sm">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600">Page</span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={currentPage}
                    onChange={(e) => {
                      const page = parseInt(e.target.value);
                      if (page >= 1 && page <= totalPages) {
                        setCurrentPage(page);
                      }
                    }}
                    className="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  />
                  <span className="text-gray-600">of {totalPages}</span>
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="flex items-center space-x-4">
                <select
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  className="text-sm border-gray-300 rounded px-4 py-1 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                >
                  <option value={25}>25 rows</option>
                  <option value={50}>50 rows</option>
                  <option value={100}>100 rows</option>
                  <option value={250}>250 rows</option>
                </select>
                
                <span className="text-gray-600">{totalRecords.toLocaleString()} records</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Dropdown */}
        <FilterDropdown 
          isOpen={showFilterDropdown}
          position={filterDropdownPosition}
          onClose={() => setShowFilterDropdown(false)}
          onAddFilters={addMultipleFilters}
          buttonRef={filterButtonRef}
          activeFilters={filters}
          onRemoveFilter={removeFilter}
          uniqueProjects={uniqueProjects}
        />

        {/* Conditional Formatting Dropdown */}
        <ConditionalFormattingDropdown
          isOpen={showFormattingDropdown}
          position={formattingDropdownPosition}
          onClose={() => setShowFormattingDropdown(false)}
          onAddRule={handleAddFormattingRule}
          buttonRef={formattingButtonRef}
          activeRules={conditionalFormattingRules}
          onRemoveRule={handleRemoveFormattingRule}
        />

        {/* Sort Modal */}
        {showSortModal && <SortModal onClose={() => setShowSortModal(false)} currentSort={{ column: sortBy, order: sortOrder }} onApplySort={(column, order) => { setSortBy(column); setSortOrder(order); }} />}

        {/* Details Modal */}
        {showDetailsModal && selectedDetailsMeasurement && (
          <DetailsModal 
            measurement={selectedDetailsMeasurement}
            onClose={() => setShowDetailsModal(false)}
          />
        )}
      </div>
    </Layout>
  );
}

